// Days-to-complete trend — FE-032 / spec §7.0.2.
//
// Per-week median completion duration. Tier 1 ships an accessible
// list of weekly samples with a small bar visualisation; the heavier
// line/area chart can replace this in a later refinement chunk.

import type { EmployeeAnalyticsResponse } from "@/features/dashboard";

import { DashboardSectionHeader } from "../primitives";

interface DaysToCompleteTrendProps {
  trend: EmployeeAnalyticsResponse["days_to_complete_trend"];
}

export function DaysToCompleteTrend({ trend }: DaysToCompleteTrendProps) {
  const sorted = [...trend].sort(
    (a, b) => (a.week_start < b.week_start ? -1 : 1),
  );
  const max = sorted.reduce(
    (m, point) => Math.max(m, point.median_days),
    0,
  );
  return (
    <section
      data-testid="days-to-complete-trend"
      aria-labelledby="days-to-complete-heading"
    >
      <DashboardSectionHeader
        title={
          <span id="days-to-complete-heading" className="text-sm font-semibold">
            Days to complete
          </span>
        }
        subtitle="Weekly median for Commitments completed in the window"
      />
      <div className="mt-2">
        {sorted.length === 0 ? (
          <p
            data-testid="days-to-complete-trend-empty"
            className="text-sm text-text-tertiary"
          >
            No completed Commitments in the selected window.
          </p>
        ) : (
          <ul
            role="list"
            className="flex h-24 items-end gap-1.5 rounded-md bg-recessed p-2"
          >
            {sorted.map((point) => {
              const heightPct =
                max === 0 ? 0 : Math.max(8, (point.median_days / max) * 100);
              return (
                <li
                  key={point.week_start}
                  className="flex flex-1 flex-col items-center justify-end gap-1"
                  data-testid={`trend-point-${point.week_start}`}
                  aria-label={
                    `Week of ${point.week_start}: ` +
                    `median ${point.median_days} days across ${point.count} Commitments`
                  }
                >
                  <span
                    className="w-full rounded-sm bg-teal-tint"
                    style={{ height: `${heightPct}%` }}
                    aria-hidden="true"
                  />
                  <span className="text-[10px] text-text-tertiary">
                    {point.week_start.slice(5)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {sorted.length > 0 && (
          <p
            data-testid="days-to-complete-trend-summary"
            className="mt-1.5 text-xs text-text-secondary"
          >
            {sorted.length} week{sorted.length === 1 ? "" : "s"} with
            completed Commitments. Latest median:{" "}
            <strong className="text-text-primary">
              {sorted[sorted.length - 1].median_days}
            </strong>{" "}
            days.
          </p>
        )}
      </div>
    </section>
  );
}
